import { useSetToast } from "@app/components/Toast";
import { Button } from "@design-system/Inputs/Button";
import { FlexRow } from "@design-system/Layout/Flex";
import { Modal } from "@design-system/Modal";
import { atom, useAtom } from "jotai";
import { useState } from "react";
import { useTranslation } from "react-i18next";
import {
    CreateSupplierInput,
    CreateSuppliersDocument,
    ResponseStatus,
    UserRole,
} from "../../../../../../generated/client/graphql";
import { useGraphQLMutation } from "../../../../../QueryClientWithHeaders";
import { useDownloadSuppliersImportTemplate } from "../../data";
import { ImportCSVButton } from "./ImportCsvButton";
import NewSupplierRow from "./NewSupplierRow";
import "./i18n";

export const newSupplierModalOpenAtom = atom(false);

export const NewSupplierModal = ({ refetch }: { refetch: () => void }) => {
    const { t } = useTranslation("NewSupplierModal");
    const { downloadSuppliersImportTemplate } =
        useDownloadSuppliersImportTemplate();

    const [open, setOpen] = useAtom(newSupplierModalOpenAtom);
    const createSuppliers = useGraphQLMutation(CreateSuppliersDocument);

    const { setToastSuccess, setToastError } = useSetToast();

    const [newSuppliers, setNewSuppliers] = useState<CreateSupplierInput[]>([
        {
            name: "",
            pointOfContact: {
                email: "",
                firstName: "",
                lastName: "",
                role: UserRole.Contributor,
            },
        },
    ]);

    const canSave =
        newSuppliers.length > 0 &&
        newSuppliers.every(
            (i) =>
                i.name.length > 0 &&
                i.pointOfContact.email.length > 0 &&
                i.pointOfContact.firstName.length > 0 &&
                i.pointOfContact.lastName.length > 0,
        );

    const onAdd = (additionalSuppliers: CreateSupplierInput[]) => {
        setNewSuppliers([
            ...newSuppliers.filter(
                (s) =>
                    s.name !== "" ||
                    s.pointOfContact.email !== "" ||
                    s.pointOfContact.firstName !== "" ||
                    s.pointOfContact.lastName !== "",
            ),
            ...additionalSuppliers,
        ]);
    };
    const onAddEmpty = () => {
        setNewSuppliers([
            ...newSuppliers,
            {
                name: "",
                pointOfContact: {
                    email: "",
                    firstName: "",
                    lastName: "",
                    role: UserRole.Supplier,
                },
            },
        ]);
    };
    const onDelete = (index: number) => {
        return () => {
            setNewSuppliers([...newSuppliers].filter((_, i) => i !== index));
        };
    };
    const onInputChange = (index: number) => {
        return (input: Partial<CreateSupplierInput>) => {
            setNewSuppliers([
                ...newSuppliers.map((supplier, idx) =>
                    idx === index ? { ...supplier, ...input } : supplier,
                ),
            ]);
        };
    };

    const onSave = () => {
        createSuppliers.mutate(
            {
                inputs: newSuppliers,
            },
            {
                onSuccess: ({ createSuppliers }) => {
                    const { status, data: createdSuppliers } = createSuppliers;
                    refetch();
                    setNewSuppliers(
                        newSuppliers.filter(
                            (s) =>
                                !createdSuppliers?.some(
                                    (c) => c.name === s.name,
                                ),
                        ),
                    );
                    if (status !== ResponseStatus.Success) {
                        setToastError(t("toast_error"));
                        return;
                    }
                    setToastSuccess(t("toast_success"));
                    setOpen(false);
                },
                onError: () => {
                    setToastError(t("toast_error"));
                },
            },
        );
    };

    const onDownloadTemplate = () => {
        downloadSuppliersImportTemplate();
    };

    const onRemoveAll = () => {
        setNewSuppliers([
            {
                name: "",
                pointOfContact: {
                    email: "",
                    firstName: "",
                    lastName: "",
                    role: UserRole.Supplier,
                },
            },
        ]);
    };

    return (
        <Modal open={open} onOpenChange={setOpen}>
            <Modal.Trigger asChild>
                <Button>{t("trigger_text")}</Button>
            </Modal.Trigger>
            <Modal.Overlay scrollable centerContent>
                <Modal.Content className="min-w-[800px] w-fit">
                    <Modal.Header>
                        <Modal.Title>{t("title")}</Modal.Title>
                        <Modal.Subtitle>{t("subtitle")}</Modal.Subtitle>
                    </Modal.Header>
                    <Modal.Body className="max-h-[75vh] overflow-y-auto">
                        {newSuppliers.map((input, i) => (
                            <NewSupplierRow
                                key={i}
                                input={input}
                                onInputChange={onInputChange(i)}
                                onDelete={onDelete(i)}
                            />
                        ))}
                    </Modal.Body>
                    <Modal.Footer className="items-center justify-between">
                        <FlexRow alignItems="center" gap="3">
                            <Button variant="tonal" onClick={onAddEmpty}>
                                {t("add_one")}
                                <Button.Icon name="userPlus" size="sm" />
                            </Button>
                            <Button variant="tonal" onClick={onRemoveAll}>
                                {t("remove_all")}
                                <Button.Icon name="trash" size="sm" />
                            </Button>
                            <ImportCSVButton onAddSuppliers={onAdd} />
                            <Button
                                variant="tonal"
                                onClick={onDownloadTemplate}
                            >
                                {t("download_template")}
                            </Button>
                        </FlexRow>
                        <FlexRow alignItems="center" gap="3">
                            <Modal.CancelCTA>
                                {t("cancel_button")}
                            </Modal.CancelCTA>
                            <Modal.ConfirmCTA
                                onClick={onSave}
                                disabled={!canSave || createSuppliers.isPending}
                                loading={createSuppliers.isPending}
                                preventClose
                            >
                                {t("save_button")}
                            </Modal.ConfirmCTA>
                        </FlexRow>
                    </Modal.Footer>
                </Modal.Content>
            </Modal.Overlay>
        </Modal>
    );
};
